import { skipToken } from "@reduxjs/toolkit/query";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { type FC, useEffect } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";
import { useAllMinimalPlaybooksQuery } from "../../api/entities/playbook.js";
import { useMyPerformanceDefaultsQuery } from "../../api/entities/teammate.js";
import { appPaths } from "../../app/appPaths.js";
import { useTeammateAuthorization } from "../../helpers/teammateAuthorizationContext.js";
import { useAppSelector } from "../../hooks/hook.js";
import { selectTeammate } from "../teammate/teammateSlice.js";

/**
 * URL segment that stands in for a real playbook ID in stable links (e.g.
 * marketing emails, which can't know a customer's playbook IDs):
 *
 *   https://app.alpharun.com/performance/playbooks/default/teammates
 *
 * Playbook IDs are UUIDs, so this segment can never collide with a real one.
 */
export const DEFAULT_PLAYBOOK_URL_SEGMENT = "default";

const PLACEHOLDER_PATH_PREFIX = appPaths.playbook.build({
  playbookId: DEFAULT_PLAYBOOK_URL_SEGMENT,
});

const CenteredMessage: FC<{ children: string }> = ({ children }) => (
  <div className="flex h-full w-full items-center justify-center">
    <div className="text-lg text-gray-600">{children}</div>
  </div>
);

export interface DefaultPlaybookRedirectProps {
  /**
   * Builds the path to redirect to once the default playbook is known.
   * Defaults to the playbook's own page.
   */
  buildPath?: (playbookId: PlaybookId) => string;
}

/**
 * Resolves the teammate's default playbook and redirects to the path
 * `buildPath` makes of it, plus the current query string and hash. The default
 * is the teammate's last selected playbook when it still exists, and otherwise
 * the first playbook. Only a teammate who has never selected one pays for the
 * smart default inferred from their reporting tree, which then takes
 * precedence over the first playbook.
 *
 * A loader stays on screen until the navigation lands, so resolving the
 * playbook never flashes an empty page.
 */
export const DefaultPlaybookRedirect: FC<DefaultPlaybookRedirectProps> = (
  props,
) => {
  const {
    buildPath = (playbookId: PlaybookId) =>
      appPaths.playbook.build({ playbookId }),
  } = props;
  const { search, hash } = useLocation();
  const navigate = useNavigate();
  const teammateAuthorization = useTeammateAuthorization();
  const {
    data: minimalPlaybooks,
    isLoading,
    error,
  } = useAllMinimalPlaybooksQuery();

  const lastSelectedPlaybookId =
    useAppSelector(selectTeammate)?.settings.preferences
      .team_performance_playbook_id;

  // Skipping the query leaves `areInferredDefaultsLoading` false, so a saved
  // preference resolves without ever waiting on the inference request. A
  // wanted-but-unstarted query already reports as loading: `useQuery` rewrites
  // its uninitialized state to pending whenever the argument isn't `skipToken`.
  const { data: inferredDefaults, isLoading: areInferredDefaultsLoading } =
    useMyPerformanceDefaultsQuery(
      lastSelectedPlaybookId == null ? undefined : skipToken,
    );

  const firstPlaybookId = minimalPlaybooks?.[0]?.id;
  const savedPlaybookId =
    lastSelectedPlaybookId &&
    minimalPlaybooks?.some((p) => p.id === lastSelectedPlaybookId)
      ? lastSelectedPlaybookId
      : undefined;
  const inferredPlaybookId =
    inferredDefaults?.playbook_id &&
    minimalPlaybooks?.some((p) => p.id === inferredDefaults.playbook_id)
      ? inferredDefaults.playbook_id
      : undefined;
  const targetPlaybookId = areInferredDefaultsLoading
    ? undefined
    : (savedPlaybookId ?? inferredPlaybookId ?? firstPlaybookId);

  // Depend on the resolved path, not on `buildPath`: call sites pass an inline
  // arrow, whose identity changes on every render.
  const targetPath =
    targetPlaybookId === undefined
      ? undefined
      : `${buildPath(targetPlaybookId)}${search}${hash}`;

  useEffect(() => {
    if (targetPath === undefined) {
      return;
    }
    navigate(targetPath, { replace: true });
  }, [navigate, targetPath]);

  if (error) {
    return (
      <CenteredMessage>
        Error loading playbooks. Please try again.
      </CenteredMessage>
    );
  }

  if (!isLoading && firstPlaybookId === undefined) {
    // Only a teammate who can create one is told to.
    const canCreatePlaybook = teammateAuthorization.can(
      PermissionActions.CREATE,
      PermissionSubjects.RECORDING_ASSESSMENT_TEMPLATES,
    );
    return (
      <CenteredMessage>
        {canCreatePlaybook
          ? "No playbooks found. Please create a playbook first."
          : "No playbooks found."}
      </CenteredMessage>
    );
  }

  // Also covers the frame(s) between resolving the playbook and the redirect
  // landing: the effect above navigates, this keeps the page from going blank.
  return (
    <div className="flex h-full w-full items-center justify-center">
      <Loader style={LoaderStyle.ZOOMIES} />
    </div>
  );
};

/**
 * The parent of every playbook-scoped route: it renders the subtree untouched,
 * except for default-playbook placeholder URLs, which it resolves to the
 * teammate's default playbook and redirects:
 *
 *   /performance/playbooks/default/teammates
 *     -> /performance/playbooks/<default playbook id>/teammates
 *
 * A parent route (rather than a route of its own) because route ranking would
 * otherwise send these URLs to the deeper `:playbookId` routes with the
 * literal string "default" as the playbook ID.
 *
 * Resolving it renders a loader in the page content, with the app chrome
 * around it — so the chrome, unlike the routes below it, does render while the
 * URL still says "default". Chrome that needs the playbook itself must read it
 * through `usePlaybookIdParam`, which yields `undefined` for the placeholder;
 * only link building may pass the raw `:playbookId` through, since a
 * placeholder link resolves here in turn.
 */
export const DefaultPlaybookGate: FC = () => {
  const { playbookId } = useParams();
  const { pathname } = useLocation();

  if (playbookId !== DEFAULT_PLAYBOOK_URL_SEGMENT) {
    return <Outlet />;
  }

  // Everything after the placeholder segment (e.g. "" or "/teammates") is
  // carried over to the resolved playbook's URL.
  const restPath = pathname.slice(PLACEHOLDER_PATH_PREFIX.length);
  return (
    <DefaultPlaybookRedirect
      buildPath={(playbookId) =>
        `${appPaths.playbook.build({ playbookId })}${restPath}`
      }
    />
  );
};
